import { useEffect, useMemo, useState } from 'react'
import Modal from './Modal'

const DEFAULT_PORTAL_URL = 'https://staff.schoolwellbeing.com/create-account/step-1'
const DEFAULT_STORAGE_KEY = 'staff_wellbeing_portal_launch_popup_dismissed_v1'

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // ignore
  }
}

async function copyToClipboard(text) {
  if (!text) return false
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) {
    // fall back below
  }

  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'absolute'
    textArea.style.left = '-9999px'
    document.body.appendChild(textArea)
    textArea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textArea)
    return ok
  } catch (e) {
    return false
  }
}

export default function StaffWellbeingPortalPromo({
  accessCode,
  portalUrl = DEFAULT_PORTAL_URL,
  storageKey = DEFAULT_STORAGE_KEY,
}) {
  const [showLaunchPopup, setShowLaunchPopup] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!storageKey) {
      setShowLaunchPopup(false)
      return
    }

    const dismissed = safeLocalStorageGet(storageKey) === '1'
    setShowLaunchPopup(!dismissed)
  }, [storageKey])

  useEffect(() => {
    if (!copied) return
    const timeout = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timeout)
  }, [copied])

  const hasCode = Boolean(accessCode)

  const accessCodeElement = useMemo(() => {
    if (!hasCode) return null

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="rounded bg-slate-100 px-3 py-2 text-slate-800 font-bold tracking-wider">
          {accessCode}
        </div>
        <button
          type="button"
          className="btn grey"
          onClick={async () => {
            const ok = await copyToClipboard(accessCode)
            setCopied(ok)
          }}
        >
          {copied ? 'Copied' : 'Copy code'}
        </button>
      </div>
    )
  }, [accessCode, copied, hasCode])

  const dismissPopup = () => {
    safeLocalStorageSet(storageKey, '1')
    setShowLaunchPopup(false)
  }

  const LaunchBody = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          We've launched our new Staff Wellbeing Portal - simple, confidential
          access to wellbeing support and resources, available 24/7.
        </p>

        <div className="text-sm text-slate-700">
          <p className="font-bold mb-2">What you can do on the portal:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Request services directly</b> - confidentially access counselling, GP consultations, and physiotherapy</li>
            <li><b>Explore on-demand wellbeing content</b> - podcasts, blogs, resources, and campaign materials</li>
            <li><b>Stay connected</b> - see upcoming webinars, events, and interactive workshops</li>
            <li><b>Access support anytime</b> - 24/7 wellbeing services and guidance</li>
            <li><b>Keep up to date</b> - monthly newsletter with tips, insights, and latest initiatives</li>
          </ul>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-bold text-slate-700">Your access code</p>
          {accessCodeElement}
          <p className="text-xs text-slate-500">
            Please share this code with staff that are named on your staff list.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-between">
          <a
            className="btn primary w-full sm:w-auto justify-center"
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open portal
          </a>
          <button
            type="button"
            className="btn clear w-full sm:w-auto justify-center"
            onClick={dismissPopup}
          >
            Hide this message
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {showLaunchPopup && (
        <Modal
          title="New: Staff Wellbeing Portal"
          body={<LaunchBody />}
          show={dismissPopup}
        />
      )}

      <div className="w-full max-w-7xl mt-8">
        <div className="bg-white rounded-md border border-wba-primary/20 shadow-sm overflow-hidden">
          <div className="border-l-4 border-wba-primary px-4 py-4 sm:px-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-800">
                    Staff Wellbeing Portal
                  </h3>
                  <span className="text-xs font-bold bg-wba-primary text-white rounded px-2 py-0.5">
                    NEW
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  We've launched our new Staff Wellbeing Portal - simple, confidential access to wellbeing support and resources, available 24/7.<br/>Please share this code with staff that are named on your staff list.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {hasCode ? (
                  accessCodeElement
                ) : (
                  <p className="text-xs text-slate-500">
                    Your school access code will appear here.
                  </p>
                )}

                <a
                  className="btn small primary justify-center"
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open portal
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
